import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import SwipeLayout from '@/app/common/filters/surfaces/bar/SwipeLayout';
import React from 'react';
import FloatingLayout from '@/app/common/filters/surfaces/bar/floating/FloatingLayout.tsx';
import { getFiltersModule } from '@/app/common/filters/variants/evo/filtersModule.ts';
import { useSearchPageWrapperData } from '@/app/components/search/searchPageCommonWrapperProvider.tsx';
import FiltersBarContainer from '@/app/components/search/components/SearchPageContainer/FiltersBarContainer';
import {
  FILTER_SURFACE,
  getCustomFilterMainCreators,
} from '@/app/common/filters/surfaces/forms/custom/customFilterMainCreators.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import type { RouteType } from '@/app/components/search/Curiosity';
import { LAYOUT_TYPE } from '@/app/components/search/components/LayoutManager/utils.ts';
import SideBar from '@/app/components/search/components/SearchPageContainer/ListContainer/SideBar';
import type Form from '@/app/components/search/components/Form';
import { ResultsHeaderShell } from '@/app/components/search/components/Title/ResultsHeaderLayout/ResultsHeaderShell';
import { ResultsHeadingBar } from '@/app/components/search/components/Title/ResultsHeaderLayout/ResultsHeadingBar';
import { ResultsSortBar } from '@/app/components/search/components/Title/ResultsHeaderLayout/ResultsSortBar';
import {
  createFloatingPillsMap,
  createFloatingPillsViewMode,
} from '@/app/common/filters/surfaces/bar/floating/createFloatingPills.ts';
import StickyOnScrollUp from '@/app/common/filters/surfaces/bar/StickyOnScrollUp';
import { SortBarInfoBadges } from '@/app/components/search/components/Title/ResultsHeaderLayout/SortBarInfoBadges.tsx';
import { MobileSortBarInfoBadgesContainer } from '@/app/components/search/components/SearchPageContainer/FiltersManager/styled.ts';
import TotalPriceToggle from '@/app/common/components/TotalPriceToggle';
import { evoFiltersEnabled, newFiltersEnabled } from '@/app/common/filters/core/filterFlags.ts';
import { useFilterBarLoading } from '@/app/common/filters/surfaces/provider/useFilterBarLoading.ts';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';

type Props = {
  isWaiting: boolean;
  routeType: RouteType;
  handleShowFilters: (newShowFilters: boolean) => void;
  layoutType: LAYOUT_TYPE;
  verticalFiltersRef?: React.MutableRefObject<Form | undefined>;
};

const FiltersManager = ({ isWaiting, routeType, handleShowFilters, layoutType, verticalFiltersRef }: Props) => {
  const { t } = useTranslation();
  const { localization, searchInfoData, searchParams } = usePageWrapperData();
  const useNewFilters = newFiltersEnabled(localization?.configValuesApp);
  const useEvoFilters = evoFiltersEnabled(localization?.configValuesApp);
  const { isDesktop, isTablet } = useCommonClient();
  const { productsData, isLoading } = useSearchPageData();
  const barLoading = useFilterBarLoading(useEvoFilters);
  // The floating pills are view-mode and All filters: fixed labels, nothing the response can correct.
  const floatingLoading = useFilterBarLoading();
  const { dynamicFiltersResponse, filtersGroups, handleFilterChanged, filterOrder } = useSearchPageWrapperData();

  // ── Legacy filter layouts ────────────────────────────────────────────────────

  if (!useNewFilters) {
    if (layoutType === LAYOUT_TYPE.DESKTOP_LIST) {
      return (
        <ErrorBoundary id="FiltersSideBar">
          <SideBar
            routeType={routeType}
            isWaiting={isWaiting}
            productsData={productsData}
            dynamicFiltersResponse={dynamicFiltersResponse?.filters}
            verticalFiltersRef={verticalFiltersRef!}
          />
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary id="Results/FiltersBarContainer">
        <FiltersBarContainer
          isDesktop={isDesktop}
          routeType={routeType}
          isWaiting={isWaiting}
          dynamicFiltersResponse={dynamicFiltersResponse?.filters}
          handleShowFilters={handleShowFilters}
        />
      </ErrorBoundary>
    );
  }

  // ── New filter layouts ───────────────────────────────────────────────────────

  const { createPills, createMobilePills, HorizontalLayoutComponent } = getFiltersModule(useEvoFilters);
  const customFilterMainCreators = getCustomFilterMainCreators(useEvoFilters, FILTER_SURFACE.PILL_OVERLAY);

  const combinedIsLoading = isWaiting || isLoading;

  const viewModeFloatingLayout = (
    <ErrorBoundary id="Results/FloatingLayout">
      <FloatingLayout
        filterPills={createFloatingPillsViewMode()}
        customFilterMainCreators={customFilterMainCreators}
        isLoading={floatingLoading.isLoading}
        handleFilterChanged={handleFilterChanged}
      />
    </ErrorBoundary>
  );

  if (layoutType === LAYOUT_TYPE.MOBILE_LIST && !isTablet) {
    const mobileFilterPills = createMobilePills({
      filtersGroups,
      searchInfoData,
      productsData,
      t,
      lang: localization.lang,
      searchParams,
      filterOrder,
    });
    return (
      <>
        <ErrorBoundary id="Results/SwipeLayout">
          <StickyOnScrollUp brand={localization.brand}>
            <SwipeLayout
              filterPills={mobileFilterPills}
              customFilterMainCreators={customFilterMainCreators}
              isLoading={barLoading.isLoading}
              handleFilterChanged={handleFilterChanged}
              scrollToStartOnChange={useEvoFilters}
            />
          </StickyOnScrollUp>
        </ErrorBoundary>
        <ErrorBoundary id="Results/SortBarInfoBadges">
          <MobileSortBarInfoBadgesContainer>
            <SortBarInfoBadges isLoading={combinedIsLoading} />
          </MobileSortBarInfoBadgesContainer>
        </ErrorBoundary>
        <ErrorBoundary id="Results/TotalPriceToggle">
          <TotalPriceToggle variant="banner" disabled={combinedIsLoading} />
        </ErrorBoundary>
        {viewModeFloatingLayout}
      </>
    );
  }

  if (layoutType === LAYOUT_TYPE.MOBILE_MAP) {
    return (
      <ErrorBoundary id="Results/FloatingLayout">
        <FloatingLayout
          filterPills={createFloatingPillsMap()}
          customFilterMainCreators={customFilterMainCreators}
          isLoading={floatingLoading.isLoading}
          handleFilterChanged={handleFilterChanged}
          isDark={false}
        />
      </ErrorBoundary>
    );
  }

  const filterPills = createPills({
    filtersGroups,
    searchInfoData,
    productsData,
    t,
    lang: localization.lang,
    searchParams,
    filterOrder,
  });

  const listFilterBar = (
    <>
      <ErrorBoundary id="Results/HorizontalFilterBar">
        <HorizontalLayoutComponent
          filterPills={filterPills}
          customFilterMainCreators={customFilterMainCreators}
          isLoading={barLoading.isLoading}
          handleFilterChanged={handleFilterChanged}
          scrollToStartOnChange={useEvoFilters}
        />
      </ErrorBoundary>
      <ErrorBoundary id="Results/ResultsSortBar">
        <ResultsSortBar variant="list" isLoading={combinedIsLoading} />
      </ErrorBoundary>
    </>
  );

  if (layoutType === LAYOUT_TYPE.MOBILE_LIST) {
    return (
      <>
        {listFilterBar}
        {viewModeFloatingLayout}
      </>
    );
  }

  if (layoutType === LAYOUT_TYPE.DESKTOP_LIST) {
    return (
      <>
        <ErrorBoundary id="Results/ResultsHeadingBar">
          <ResultsHeadingBar isWaiting={isWaiting} variant="list" />
        </ErrorBoundary>
        {listFilterBar}
      </>
    );
  }

  // DESKTOP_MAP
  return (
    <ResultsHeaderShell isWaiting={isWaiting}>
      <ErrorBoundary id="Results/HorizontalFilterBar">
        <HorizontalLayoutComponent
          filterPills={filterPills}
          customFilterMainCreators={customFilterMainCreators}
          isLoading={barLoading.isLoading}
          handleFilterChanged={handleFilterChanged}
          scrollToStartOnChange={useEvoFilters}
        />
      </ErrorBoundary>
      <ErrorBoundary id="Results/ResultsHeadingBar">
        <ResultsHeadingBar isWaiting={isWaiting} variant="map" />
      </ErrorBoundary>
      <ErrorBoundary id="Results/ResultsSortBar">
        <ResultsSortBar isLoading={combinedIsLoading} />
      </ErrorBoundary>
    </ResultsHeaderShell>
  );
};

export default FiltersManager;
